import { apiFetch } from "@/lib/api";
import { useAuthStore, type AuthUser } from "@/features/auth/store/auth-store";

/**
 * The Django API wraps every body in a standard envelope
 * (`APIJSONRenderer`): `{ success, message, data }`. `apiFetch` returns that
 * envelope verbatim, so this layer reads `.data`. (See the api-envelope handling
 * used by `features/clients/api.ts` — same pattern.)
 */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface SessionResponse {
  access: string;
  refresh: string;
  user: AuthUser;
}

/** Login/verify either return a full session, or signal the email is unverified. */
type SessionOrVerification = SessionResponse | { email_verification_required: true; email: string };

function isVerificationRequired(
  data: SessionOrVerification,
): data is { email_verification_required: true; email: string } {
  return "email_verification_required" in data && data.email_verification_required;
}

/** Outcome of a login attempt the UI branches on. */
export type LoginResult =
  | { status: "authenticated"; user: AuthUser }
  | { status: "verification_required"; email: string };

/**
 * POST /auth/login. On a verified account this stores the session and returns
 * `authenticated`. On an unverified account the backend withholds tokens and
 * (re)sends an OTP, and we return `verification_required` so the caller can
 * route to the verify-email step instead.
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  const { data } = await apiFetch<Envelope<SessionOrVerification>>("/auth/login", {
    method: "POST",
    auth: false,
    json: { email, password },
  });

  if (isVerificationRequired(data)) {
    return { status: "verification_required", email: data.email };
  }

  useAuthStore.getState().setSession({
    user: data.user,
    accessToken: data.access,
    refreshToken: data.refresh,
  });
  return { status: "authenticated", user: data.user };
}

/**
 * POST /auth/verify-email — exchanges the emailed OTP for a session. Verifying
 * logs the user in (backend returns tokens), so this stores the session and the
 * user lands straight in the app.
 */
export async function verifyEmailOtp(email: string, code: string): Promise<AuthUser> {
  const { data } = await apiFetch<Envelope<SessionResponse>>("/auth/verify-email", {
    method: "POST",
    auth: false,
    json: { email, code },
  });

  useAuthStore.getState().setSession({
    user: data.user,
    accessToken: data.access,
    refreshToken: data.refresh,
  });
  return data.user;
}

/**
 * POST /auth/password-reset — asks the backend to email a reset link. The
 * response is always generic (never reveals whether the address exists), so this
 * resolves regardless of whether an account was found.
 */
export function requestPasswordReset(email: string): Promise<void> {
  return apiFetch<void>("/auth/password-reset", {
    method: "POST",
    auth: false,
    json: { email },
  });
}

/**
 * POST /auth/password-reset/confirm — sets a new password from the emailed
 * single-use token. Does not log the user in (all sessions are revoked); the
 * caller sends them to /login afterwards.
 */
export function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  return apiFetch<void>("/auth/password-reset/confirm", {
    method: "POST",
    auth: false,
    json: { token, new_password: newPassword },
  });
}

/** POST /auth/resend-otp — asks the backend to email a fresh verification code. */
export function resendOtp(email: string): Promise<void> {
  return apiFetch<void>("/auth/resend-otp", {
    method: "POST",
    auth: false,
    json: { email },
  });
}

/**
 * POST /auth/logout — best-effort blacklist of the refresh token, then clears
 * local state regardless of the server outcome (a dead/expired token still
 * means the user is logged out locally).
 */
export async function logout(): Promise<void> {
  const { refreshToken, clearSession } = useAuthStore.getState();
  try {
    if (refreshToken) {
      await apiFetch<void>("/auth/logout", { method: "POST", json: { refresh: refreshToken } });
    }
  } catch {
    // ignore — clearing local state below is what matters
  } finally {
    clearSession();
  }
}
